import React, { useEffect, useRef, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { theme } from 'styled-tools';
import { withAppContext } from 'src/store/initAppContext';
import cookieUtil from 'src/util/cookieUtil';
import { ECookieName } from 'src/util/utilModel';
import config from 'src/config/config';
import CheckedLang from '../icon/CheckedLang';

const languages = [
    {
        value: 'en-us',
        shortName: 'EN',
        name: 'English',
        image: 'englishFlag.png',
    },
    {
        value: 'hindi-ind',
        shortName: 'HIN',
        name: 'Hindi',
        image: 'indiaFlag.png',
    },
    {
        value: 'tel',
        shortName: 'TEL',
        name: 'Telugu',
        image: 'telugu_lang_icon.png',
    },
];

function BeforeLoginLanguage(props) {
    const { app: { brandId } } = props;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);

    const [selectedLanguage] = useState(() => {
        const currentLanguage = cookieUtil.get(ECookieName.COOKIE_LANG);

        return languages.find(
            language => language.value === currentLanguage
        ) || languages[0];
    });

    const isFun88 = [46, 47].includes(Number(brandId));

    useEffect(() => {
        if (!open) return;

        const handleOutsideClick = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [open]);

    const changeLanguage = (language: string) => {
        setOpen(false);

        if (language === selectedLanguage.value) return;

        // Same cookie and reload flow as the existing selector.
        cookieUtil.remove(ECookieName.COOKIE_LANG);
        cookieUtil.set(ECookieName.COOKIE_LANG, language, 30);
        window.location.reload();
    };

    return (
        <LanguageWrapper
            ref={wrapperRef}
            $isFun88={isFun88}
            $dividerColor={isFun88 ? '#fff' : '#e5e5e5'}
            onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                    setOpen(false);
                }
            }}
            onKeyDown={event => {
                if (event.key === 'Escape') {
                    setOpen(false);
                    triggerRef.current?.focus();
                }
            }}
        >
            <LanguageTrigger
                ref={triggerRef}
                type="button"
                $isFun88={isFun88}
                aria-label={`Choose language. Current language: ${selectedLanguage.name}`}
                aria-expanded={open}
                aria-controls="before-login-language-options"
                onClick={() => setOpen(previous => !previous)}
            >
                <img
                    src={`${config.imageV3Host}/static/media/${selectedLanguage.image}`}
                    alt=""
                />
                <span>{selectedLanguage.shortName}</span>
            </LanguageTrigger>

            {open && (
                <LanguageDropdown id="before-login-language-options">
                    {languages.map(language => {
                        const selected =
                            language.value === selectedLanguage.value;

                        return (
                            <LanguageOption
                                key={language.value}
                                type="button"
                                $selected={selected}
                                aria-pressed={selected}
                                onClick={() => changeLanguage(language.value)}
                            >
                                <img
                                    src={`${config.imageV3Host}/static/media/${language.image}`}
                                    alt=""
                                />

                                <span>{language.name}</span>

                                {selected && (
                                    <SelectedMark aria-hidden="true">
                                        <CheckedLang/>
                                    </SelectedMark>
                                )}
                            </LanguageOption>
                        );
                    })}
                </LanguageDropdown>
            )}
        </LanguageWrapper>
    );
}

export default withAppContext(withTheme(BeforeLoginLanguage));

const LanguageWrapper = styled.div<{
    $dividerColor: string;
    $isFun88: boolean;
}>`
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
    flex-shrink: 0;
    margin-left: ${props => props.$isFun88 ? '14px' : '18px'};
    margin-right: ${props => props.$isFun88 ? '-14px' : '0'};
    padding-left: 1px;
    padding-right: ${props => props.$isFun88 ? '10px' : '0'};
    background: ${props => props.$isFun88
        ? props.theme.palette.AppDwnldWrapperHeaderBg
        : 'transparent'};
    z-index: 10;

    &::before {
        content: '';
        position: absolute;
        left: ${props => props.$isFun88 ? '-14px' : '-14px'};
        top: 0;
        width: ${props => props.$isFun88 ? '27px' : '0'};
        height: 100%;
        background: ${props => props.$isFun88
            ? props.theme.palette.AppDwnldWrapperHeaderBg
            : 'transparent'};
        border-left: 2px solid ${props => props.$dividerColor};
        transform: skew(-20deg);
        pointer-events: none;
        z-index: -1;
    }
`;

const LanguageTrigger = styled.button<{$isFun88: boolean;}>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    // min-width: ${props => props.$isFun88 ? '90px' : '78px'};
    // height: ${props => props.$isFun88 ? '46px' : '42px'};
    padding: 5px 9px;
    border: none;
    border-radius: 28px;
    background: linear-gradient(90deg, #e7e7e7, #f3f3f3);
    color: ${theme('palette.beforeLoginLanguageTextColor')};
    font-family: inherit;
    font-size: ${props => props.$isFun88 ? '13px' : '13px'};
    font-weight: 700;
    cursor: pointer;

    span {
        font-size: 12px;
        font-family: 'Inter-Bold';
    }

    img {
        width: ${props => props.$isFun88 ? '25px' : '25px'};
        height: ${props => props.$isFun88 ? '25px' : '25px'};
        object-fit: contain;
        border-radius: 50%;
    }

    &:focus-visible {
        outline: none;
        outline-offset: unset;
    }
`;

const LanguageDropdown = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    width: 145px;
    padding: 6px 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    box-shadow: 0 23px 27.4px 0 rgba(0, 0, 0, 0.25);
    z-index: 20;
`;

const LanguageOption = styled.button<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 48px;
    padding: 8px 4px;
    border: none;
    border-bottom: 1px solid #e5e5e5;
    background: transparent;
    color: ${props => props.$selected ? '#242424' : '#808080'};
    font-family: inherit;
    font-size: 13px;
    font-weight: ${props => props.$selected ? 700 : 400};
    text-align: left;
    cursor: pointer;

    // span {
    //     font-size: 12px;
    //     font-family: 'Inter-Bold';
    // }

    img {
        width: 25px;
        height: 25px;
        object-fit: contain;
        border-radius: 50%;
        flex-shrink: 0;
    }

    &:last-child {
        border-bottom: none;
    }

    &:hover,
    &:focus-visible {
        background: #f5f5f5;
        border-radius: 4px;
    }
`;

const SelectedMark = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    // margin-left: 10px;
    font-size: 11px;

    svg {
        width: 12px;
        height: 14px;
    }
`;